import classNames from "classnames";
import { useSelector } from "react-redux";
import { Box, Grid, makeStyles, Typography, FormControl } from "@material-ui/core";

import IconCard from "../../iconCard";
import { COLORS } from "../../../../../theme/colors";
import { MGMT_COMPANY } from "../../../../../services/constants";
import { TextInput, Select } from "../../../../../components/base";
import ownerIcon from "../../../../../assets/images/icons/owner.svg";
import ownerIconSelected from "../../../../../assets/images/icons/workspace/owner-selected.svg";
import pmcIcon from "../../../../../assets/images/icons/sub-meter-and-public.svg";
import pmcIconSelected from "../../../../../assets/images/icons/workspace/pmc-selected.svg";
import bothIcon from "../../../../../assets/images/icons/workspace/both.svg";
import bothIconSelected from "../../../../../assets/images/icons/workspace/both-selected.svg";
import { getDateFromNow, getYearOffsetFromNow } from "../../../../../services/utils";

const icons = [
  {
    id: "OWNER",
    icon: ownerIcon,
    iconSelected: ownerIconSelected,
    label: "Building Owner",
  },
  {
    id: MGMT_COMPANY,
    icon: pmcIcon,
    iconSelected: pmcIconSelected,
    label: "Mgmt. Company",
  },
  {
    id: "BOTH",
    icon: bothIcon,
    iconSelected: bothIconSelected,
    label: "Both",
  },
];

const initialOwnership = ["FREE_HOLDER", "LEASE_HOLDER"];
const ownershipOptionNames = ["Freehold", "Leasehold"];

const useStyles = makeStyles((theme) => ({
  heading: {
    fontSize: 30,
    fontWeight: 700,
  },
  topBox: {
    marginTop: 34,
  },
  box: {
    display: "flex",
    marginTop: 10,
  },
  iconText: {
    padding: theme.spacing(0.5),
    display: "flex",
    width: 270,
    "&>:nth-child(1)": {
      borderRadius: "5px 0px 0px 5px",
    },
    "&>:nth-child(3)": {
      borderRadius: "0px 5px 5px 0px",
    },
  },
  netShare: {
    padding: theme.spacing(0.5),
    display: "flex",
    width: 120,
  },
  levelBox: {
    width: 72,
    background: COLORS.gray[0],
    display: "flex",
    justifyContent: "center",
    fontSize: 18,
    paddingTop: 8,
    paddingBottom: 8,
    color: COLORS.white,
    cursor: "pointer",
    borderRight: "2px solid",
    borderColor: COLORS.white,
  },
  low: {
    background: COLORS.green,
  },
  med: {
    background: COLORS.maize,
  },
  high: {
    background: COLORS.lightCarminePink,
  },
  line: {
    height: 1,
    marginTop: 34,
    marginBottom: 30,
    marginLeft: 0,
    width: 545,
    backgroundColor: COLORS.darks[0][10],
  },
  bottomHeading: {
    display: "flex",
    fontSize: 18,
  },
  boldData: {
    fontWeight: 700,
    fontSize: 16,
  },
  text: {
    fontSize: 16,
  },
  rightDiv: {
    "& .MuiGrid-justify-content-xs-center": {
      justifyContent: "flex-start",
    },
  },
  selectorRoot: {
    padding: 10,
    backgroundColor: COLORS.white,
    textTransform: "capitalize",
  },
}));

const BuildingData = ({ building, onBuildingChange }) => {
  const classes = useStyles();
  const { user } = useSelector((state) => state.auth);
  const currencySymbol = user.currency.symbol;
  const workingStatus = building.building_info?.working_status;

  const onBuildingInfoChange = (key, value) => {
    onBuildingChange("building_info", {
      ...building.building_info,
      [key]: value,
    });
  };

  const onDebtDataChange = (key, value) => {
    onBuildingChange("debtdata", {
      ...building.debtdata,
      [key]: value,
    });
  };

  return (
    <Box>
      <Typography className={classes.heading}>{user.clusters[0].name} is...</Typography>
      <Box display="flex" mt={4}>
        {icons.map((data) => {
          return (
            <IconCard
              key={data.id}
              id={data.id}
              height={50}
              width={50}
              src={workingStatus === data.id ? data.iconSelected : data.icon}
              selected={workingStatus === data.id}
              style={{ background: workingStatus === data.id ? COLORS.purples[0] : COLORS.white }}
              text={data.label}
              onClick={(v) => onBuildingInfoChange("working_status", v)}
            />
          );
        })}
      </Box>
      {(workingStatus === icons[0].id || workingStatus === icons[2].id) && (
        <Box className={classes.topBox}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={4}>
              <Typography variant="h4">Your net share in the building:</Typography>
            </Grid>
            <Grid item xs={2}>
              <Box className={classes.netShare}>
                <TextInput
                  fullWidth
                  type="number"
                  value={building.building_info?.net_share}
                  onChange={(v) => onBuildingInfoChange("net_share", v)}
                  style={{ background: COLORS.white }}
                  containerStyle={classes.box}
                  suffix={"%"}
                />
              </Box>
            </Grid>
          </Grid>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={4}>
              <Typography variant="h4">Type of Ownership:</Typography>
            </Grid>
            <Grid item xs={2}>
              <Box className={classes.netShare}>
                <FormControl className={classes.selectContainer} variant="outlined">
                  <Select
                    rootStyle={classes.selectorRoot}
                    options={initialOwnership}
                    value={building.building_info?.ownership}
                    optionNames={ownershipOptionNames}
                    onChange={(v) => onBuildingInfoChange("ownership", v)}
                    downArrow
                  />
                </FormControl>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
      <Box className={classes.line} />
      <Box className={classes.midBox}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={4}>
            <Typography variant="h4">Building’s investment risk level:</Typography>
          </Grid>
          <Grid item xs={4}>
            <Box className={classes.iconText}>
              <Typography
                className={classNames(building.risk === "LOW" && classes.low, classes.levelBox)}
                onClick={() => onBuildingChange("risk", "LOW")}
              >
                Low
              </Typography>
              <Typography
                className={classNames(building.risk === "MEDIUM" && classes.med, classes.levelBox)}
                onClick={() => onBuildingChange("risk", "MEDIUM")}
              >
                Med
              </Typography>
              <Typography
                className={classNames(building.risk === "HIGH" && classes.high, classes.levelBox)}
                onClick={() => onBuildingChange("risk", "HIGH")}
              >
                High
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={4}>
            <Typography variant="h4">Building’s est. current market value:</Typography>
          </Grid>
          <Grid item xs={6} className={classes.rightDiv}>
            <Box className={classes.iconText}>
              <TextInput
                fullWidth
                type="number"
                value={building.market_value}
                onChange={(v) => onBuildingChange("market_value", v)}
                style={{ background: COLORS.white }}
                containerStyle={classes.box}
                suffix={currencySymbol}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box className={classes.line} />
      <Box className={classes.bottomBox}>
        <Box className={classes.bottomHeading}>
          <Typography className={classes.boldData}>Debt Data</Typography>
          &nbsp;
          <Typography style={{ fontSize: 16 }}>(Optional)</Typography>
        </Box>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={4}>
            <Typography className={classes.text}>Loan</Typography>
          </Grid>
          <Grid item xs={4} className={classes.rightDiv}>
            <Box className={classes.iconText}>
              <TextInput
                fullWidth
                type="number"
                value={building.debtdata?.loan}
                onChange={(v) => onDebtDataChange("loan", v)}
                style={{ background: COLORS.white }}
                containerStyle={classes.box}
                suffix={currencySymbol}
              />
            </Box>
          </Grid>
        </Grid>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={4}>
            <Typography className={classes.text}>Term of loan (years till end)</Typography>
          </Grid>
          <Grid item xs={4} className={classes.rightDiv}>
            <Box className={classes.iconText}>
              <TextInput
                fullWidth
                type="number"
                value={getYearOffsetFromNow(building.debtdata?.term_loan)}
                onChange={(v) =>
                  onDebtDataChange("term_loan", v ? getDateFromNow(parseInt(v), "years") : null)
                }
                style={{ background: COLORS.white }}
                containerStyle={classes.box}
                suffix={"Years"}
              />
            </Box>
          </Grid>
        </Grid>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={4}>
            <Typography className={classes.text}>Interest rate</Typography>
          </Grid>
          <Grid item xs={4} className={classes.rightDiv}>
            <Box className={classes.iconText}>
              <TextInput
                fullWidth
                type="number"
                value={building.debtdata?.interest_rate}
                onChange={(v) => onDebtDataChange("interest_rate", v)}
                style={{ background: COLORS.white }}
                containerStyle={classes.box}
                suffix={"%"}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default BuildingData;
